import _ from "lodash";
import React, { Fragment, memo } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

import { InternetSecurity } from "./components/internetSecurity/InternetSecurity";
import { WebFilters } from "./components/webFilters/WebFilters";
import { PROFILE_TAB_ACTIVE_CLASS } from "./components/profileTab/ProfileTab";
import { ProfileLink } from "./components/profileLink/ProfileLink";
import { ScheduleContainer } from "./components/schedule/ScheduleContainer";
import { Button } from "../../components/button/Button";
import { SettingsSidebarNavigation } from "../../components/settingsSidebarNavigation/SettingsSidebarNavigation";
import { Counter } from "../../components/counter/Counter";
import {
  useAddProfileAction,
  useDeleteProfileItem,
  useEditProfileItem,
} from "./components/hooks";
import {
  getProfileById,
  getVisibleProfiles,
  getVisibleProfilesWithKeys,
} from "../../store/profiles/profiles.selectors";
import { useIntlTranslate } from "../../hooks";
import { ProfileType } from "../../store/profiles/profiles.types";

import "./Profiles.scss";

const ProtectionPreferencesPageTitle = (props) => {
  return (
    <h1 {...props}>
      <FormattedMessage id={"protection_preferences"} />
    </h1>
  );
};

export const ActionsSection = ({
  profileLimit,
  addDialogWillShow,
  selectedProfileId,
}) => {
  const translate = useIntlTranslate();
  const profilesLength = useSelector(
    (state) => getVisibleProfiles(state).length
  );
  const selectedProfile = useSelector(
    getProfileById(selectedProfileId),
    _.isEqual
  );

  const actionItems = [
    useEditProfileItem(selectedProfile),
    useDeleteProfileItem(selectedProfile),
  ];
  const addProfileItem = useAddProfileAction();

  const handleAddClick = (action) => {
    if (addDialogWillShow) {
      addDialogWillShow(action);
    } else {
      action();
    }
  };

  return (
    <Fragment>
      {selectedProfile.type === ProfileType.Custom
        ? _.map(actionItems, ({ id, mobileContent, action }) => (
            <Button
              key={id}
              color={"secondary"}
              size={"large"}
              onClick={action}
            >
              {mobileContent}
            </Button>
          ))
        : null}
      <Button
        color={"secondary"}
        size={"large"}
        disabled={profilesLength >= profileLimit}
        onClick={() => handleAddClick(addProfileItem.action)}
        aria-label={translate("aria_create_new_group")}
      >
        {addProfileItem.mobileContent}
      </Button>
    </Fragment>
  );
};

const ProfilesTab = ({ profile, ...props }) => {
  const menuItems = [
    useEditProfileItem(profile),
    useDeleteProfileItem(profile),
  ];

  return (
    <ProfileLink
      type={profile.type}
      menu={profile.type === ProfileType.Custom ? menuItems : null}
      title={profile.type !== ProfileType.Custom ? profile.type : profile.name}
      to={`/settings/profiles/${profile.id}`}
      {...props}
    />
  );
};

export const ProfileContent = ({ profileId }) => {
  return (
    <section className={"profiles__content"}>
      <WebFilters profileId={profileId} className="profiles__section" />
      <ScheduleContainer profileId={profileId} className="profiles__section" />
      <InternetSecurity profileId={profileId} className="profiles__section" />
    </section>
  );
};

export const MenuHeader = memo(
  ({
    profileLimit,
    profilesLength,
    currentAmount,
    addDialogWillShow,
    isWizard,
  }) => {
    const translate = useIntlTranslate();
    const addProfileItem = useAddProfileAction();
    const handleAddClick = () => {
      if (addDialogWillShow) {
        addDialogWillShow(addProfileItem);
      } else {
        addProfileItem.action();
      }
    };

    return (
      <div className={"sidebar__header"}>
        {isWizard ? (
          <h1
            className={"sidebar__header--main"}
            aria-label={translate("aria_wizard_sidebar_header_title")}
          >
            <FormattedMessage id={"wizard_sidebar_header_title"} />
          </h1>
        ) : (
          <ProtectionPreferencesPageTitle className={"sidebar__header--main"} />
        )}
        <div className={"sidebar__add-group"}>
          <Counter
            className={"sidebar__counter"}
            label={
              <h2>
                <FormattedMessage id={"profiles"} />
              </h2>
            }
            amount={currentAmount}
            limit={profileLimit}
          />
          <Button
            color={"secondary"}
            disabled={profilesLength >= profileLimit}
            onClick={handleAddClick}
            aria-label={translate("aria_create_new_group")}
          >
            {addProfileItem.content}
          </Button>
        </div>
      </div>
    );
  }
);

export const Profiles = ({
  selectedProfileId,
  limit,
  addDialogWillShow,
  customProfilesAmount,
}) => {
  const profiles = useSelector(
    getVisibleProfilesWithKeys(["id", "name", "type"]),
    _.isEqual
  );

  const profileLimit = limit - 1; // 1 means hidden group for blocked devices
  const currentAmount = customProfilesAmount + 2; //TODO: 2 means default groups visitors and employees

  if (profiles.length === 1) {
    return (
      <div className="profiles profiles--single">
        <ProtectionPreferencesPageTitle className={"profiles__title"} />
        <ProfileContent profileId={selectedProfileId} />
      </div>
    );
  }

  const prepareTabs = () =>
    _.map(profiles, (profile) => {
      return {
        id: profile.id,
        header: <ProfilesTab profile={profile} />,
        content: <ProfileContent profileId={profile.id} />,
        name: profile.name,
        type: profile.type,
      };
    });

  return (
    <div className="profiles profiles--multiple">
      <SettingsSidebarNavigation
        tabs={prepareTabs()}
        activeTabClass={PROFILE_TAB_ACTIVE_CLASS}
        sidebarMenuHeader={
          <MenuHeader
            profileLimit={profileLimit}
            profilesLength={profiles.length}
            currentAmount={currentAmount}
            addDialogWillShow={addDialogWillShow}
          />
        }
        counter={
          <Counter
            className={"sidebar__counter"}
            label={
              <h2>
                <FormattedMessage id={"profiles"} />
              </h2>
            }
            amount={currentAmount}
            limit={profileLimit}
          />
        }
        sideSection={
          <ActionsSection
            selectedProfileId={selectedProfileId}
            profileLimit={profileLimit}
            addDialogWillShow={addDialogWillShow}
          />
        }
      />
    </div>
  );
};

Profiles.propTypes = {
  // Awkward name points to the fact that this is doubtful solution
  addDialogWillShow: PropTypes.func,
};
